import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { merge } from 'lodash';
import Vega from 'react-vega';
import { ThemeContext, vegaDarkTheme } from 'constants/theme';

// "import * as" is necessary to overcome vega-tooltip import errors
// due to how the library it is exported
import * as vegaTooltip from 'vega-tooltip';

import styles from './chart-item.less';

const CHART_DEFAULT_WIDTH = 160;
const CHART_DEFAULT_HEIGHT = 40;

const vegaTooltipHandler = {
  light: new vegaTooltip.Handler({
    theme: 'mongodb-charts-light',
  }),
  dark: new vegaTooltip.Handler({
    theme: 'mongodb-charts-dark',
  }),
};

class ChartItem extends PureComponent {
  static propTypes = {
    data: PropTypes.shape({
      documents: PropTypes.array,
    }),
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    padding: PropTypes.object,
    className: PropTypes.string,
    reductions: PropTypes.object.isRequired,
    renderer: PropTypes.oneOf(['svg', 'canvas']),
    spec: PropTypes.object.isRequired,
  };

  static defaultProps = {
    data: {
      documents: [],
    },
    renderer: 'canvas',
    className: '',
  };

  /**
   * Returns the final spec and data formatted correctly for the given spec type.
   *
   * @returns {Object} - an object containing the final spec and data to be consumed by the ChartClass
   **/
  _getSpecAndData() {
    const { width, height } = this._calculateChartDimensions();

    // Combine the encoded spec with the width, height and proper temporal label format to produce a final spec
    const spec = { ...this.props.spec, width, height };

    const { documents } = this.props.data;
    const data = { values: documents };

    return { spec, data };
  }

  _parseError = err => {
    // eslint-disable-next-line no-console
    console.error(`Vega failed to render due to a parseError. ${err}`);
  };

  /**
   * Calculates the appropriate Chart Diemensions by taking into account the given width / height (size of parent),
   * and the size of the Axis Label, computing new dimensions.
   * @returns {Object} - calculated width and height
   **/
  _calculateChartDimensions() {
    const width = Math.max(this.props.width, CHART_DEFAULT_WIDTH);
    const height = Math.max(this.props.height, CHART_DEFAULT_HEIGHT);

    return { width, height };
  }

  /**
   * renders the chart as a ReactVega component.
   *
   * @return {Component}   the ReactVega component.
   */
  render() {
    const { className, padding, renderer } = this.props;
    const { width, height } = this._calculateChartDimensions();
    const { spec, data } = this._getSpecAndData();
    const chartClassNames = classnames(styles.chart, className);
    return (
      <ThemeContext.Consumer>
        {theme => (
          <Vega
            spec={merge(
              {},
              spec,
              theme === 'dark' ? { config: vegaDarkTheme } : {}
            )}
            data={data}
            width={width}
            height={height}
            padding={padding}
            className={chartClassNames}
            renderer={renderer}
            tooltip={vegaTooltipHandler[theme].call}
            onParseError={this._parseError}
          />
        )}
      </ThemeContext.Consumer>
    );
  }
}

export default ChartItem;
export { ChartItem };
